import { httpClient } from "@/shared/api";

import type { Client, ClientInput } from "../model/types";
import type {
  ClientResponseDTO,
  CreateClientRequestDTO,
  ListClientsResponseDTO,
} from "./dto";

/** DTO (snake_case) → domain Client (camelCase). Single mapping point. */
function toDomain(dto: ClientResponseDTO): Client {
  return {
    id: dto.id,
    firstName: dto.first_name,
    lastName: dto.last_name,
    phone: dto.phone,
    email: dto.email,
  };
}

/**
 * Request body builder — shared by create and update (PATCH = full replace).
 * Always includes: first_name, phone (trimmed).
 * Omits last_name / email when empty after trim: avoids format:email on "" and
 * signals the server to clear those fields on PATCH.
 */
export function buildClientRequestBody(
  input: ClientInput,
): CreateClientRequestDTO {
  const body: CreateClientRequestDTO = {
    first_name: input.firstName.trim(),
    phone: input.phone.trim(),
  };
  const lastName = input.lastName.trim();
  const email = input.email.trim();
  if (lastName) {
    body.last_name = lastName;
  }
  if (email) {
    body.email = email;
  }
  return body;
}

/**
 * `GET /api/clients[?q=]` — all clients for the session company (RLS-scoped server-side).
 * Appends ?q= only when q is non-empty after trim.
 * Accepts an AbortSignal for stale-search cancellation.
 * 401 handled by the global interceptor (ADR-0006 D4); every reject is already
 * normalized to AppError (ADR-0010 D4).
 * Returns `{ clients, limited }` — `limited` signals server-side truncation.
 */
export async function listClients(
  q?: string,
  signal?: AbortSignal,
): Promise<{ clients: Client[]; limited: boolean }> {
  const { data } = await httpClient.get<ListClientsResponseDTO>("/clients", {
    params: q?.trim() ? { q: q.trim() } : undefined,
    signal,
  });
  return { clients: data.clients.map(toDomain), limited: data.limited };
}

/** `GET /api/clients/{id}` — single client; 200 → domain Client. */
export async function getClient(id: string): Promise<Client> {
  const { data } = await httpClient.get<ClientResponseDTO>(`/clients/${id}`);
  return toDomain(data);
}

/** `POST /api/clients` (CSRF) — creates client; 201 → domain Client. */
export async function createClient(input: ClientInput): Promise<Client> {
  const body = buildClientRequestBody(input);
  const { data } = await httpClient.post<ClientResponseDTO>("/clients", body);
  return toDomain(data);
}

/**
 * `PATCH /api/clients/{id}` (CSRF) — full replace of client profile; 200 → domain Client.
 * Omitted optional fields are cleared by the server.
 */
export async function updateClient(
  id: string,
  input: ClientInput,
): Promise<Client> {
  const body = buildClientRequestBody(input);
  const { data } = await httpClient.patch<ClientResponseDTO>(
    `/clients/${id}`,
    body,
  );
  return toDomain(data);
}

/** `DELETE /api/clients/{id}` (CSRF) — hard-delete; 204 no body. */
export async function deleteClient(id: string): Promise<void> {
  await httpClient.delete(`/clients/${id}`);
}
